import { Button as CompassButton } from "@circleco/compass/components/Button";
import type { IconName } from "@circleco/compass/components/Icon";
import { allowedClassesFor } from "@circleco/compass/helpers/allowedClassesFor";
import type { ComponentPropsWithRef } from "react";

import type { ButtonProps as UIKitButtonProps } from "@/react/components/shared/uikit/Button";
import { Button as UIKitButton } from "@/react/components/shared/uikit/Button";
import type { ButtonVariant as UIKitButtonVariant } from "@/react/components/shared/uikit/Button";
import { useIsCircleV4FeatureEnabled } from "@/react/hooks/useIsCircleV4FeatureEnabled";

type CompassButtonProps = ComponentPropsWithRef<typeof CompassButton>;
export type ButtonVariant = UIKitButtonVariant | "branded";

export interface ButtonProps extends Omit<UIKitButtonProps, "variant"> {
  variant?: ButtonVariant;
}

interface CompassCompatButtonProps extends ButtonProps {
  endIcon?: IconName;
  startIcon?: IconName;
}

const mapVariant = (
  variant: ButtonVariant | undefined,
): CompassButtonProps["variant"] => {
  switch (variant) {
    case "primary":
      return "primary";
    case "branded":
      return "branded";
    case "secondary":
      return "secondary";
    case "secondary-light":
      return "outline";
    case "tertiary":
      return "ghost";
    case "danger":
      return "destructive";
    case "danger-outline":
      return "destructive";
    case "circle":
      return "primary";
    case "circle-plain":
      return "ghost";
    case "marketing-gradient":
      return "gradient";
    case "marketing-gradient-blue":
      return "gradient";
    case "plain":
    case "text":
    case "circle-text":
      return "ghost";
    case "link":
      return "ghost";
    default:
      return "primary";
  }
};

const mapSize = (
  small?: boolean,
  large?: boolean,
  extraLarge?: boolean,
): CompassButtonProps["size"] => {
  if (extraLarge) {
    return "lg";
  }
  if (large) {
    return "lg";
  }
  if (small) {
    return "sm";
  }
  return "md";
};

export const Button = (props: CompassCompatButtonProps) => {
  const isCircleV4 = useIsCircleV4FeatureEnabled("circle_v4_buttons_enabled");

  const { startIcon, endIcon, ...restProps } = props;

  if (isCircleV4) {
    const {
      variant,
      small: isSmall,
      large: isLarge,
      extraLarge: isExtraLarge,
      full: isFull,
      forwardedRef,
      className,
      type = "submit",
      ...restProps
    } = props;

    const compassVariant = mapVariant(variant);
    const compassSize = mapSize(isSmall, isLarge, isExtraLarge);

    const compassProps: CompassButtonProps = {
      ...restProps,
      variant: compassVariant,
      size: compassSize,
      ref: forwardedRef,
      fullWidth: isFull,
      startIcon,
      endIcon,
      className: className ? allowedClassesFor("Button", className) : "",
      type,
    };

    return <CompassButton {...compassProps} />;
  }

  const { variant, ...uikitProps } = restProps;
  const uikitVariant: UIKitButtonVariant =
    !variant || variant === "branded" ? "primary" : variant;

  return <UIKitButton {...uikitProps} variant={uikitVariant} />;
};
